// Copyright (C) 2021-2022 Intel Corporation
// Copyright (C) 2023-2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import Layout from 'antd/lib/layout';
import Spin from 'antd/lib/spin';
import notification from 'antd/lib/notification';
import Button from 'antd/lib/button';

import './styles.scss';
import AttributeAnnotationWorkspace from 'components/annotation-page/attribute-annotation-workspace/attribute-annotation-workspace';
import ReviewAnnotationsWorkspace from 'components/annotation-page/review-workspace/review-workspace';
import StandardWorkspaceComponent from 'components/annotation-page/standard-workspace/standard-workspace';
import StandardWorkspace3DComponent from 'components/annotation-page/standard3D-workspace/standard3D-workspace';
import TagAnnotationWorkspace from 'components/annotation-page/tag-annotation-workspace/tag-annotation-workspace';
import FiltersModalComponent from 'components/annotation-page/top-bar/filters-modal';
import StatisticsModalComponent from 'components/annotation-page/top-bar/statistics-modal';
import FrameStripContainer from 'components/annotation-page/workspace/frame-strip-container';
import ReferenceStripContainer from 'components/annotation-page/workspace/reference-strip-container';
import JobNotFound from 'components/annotation-page/workspace/job-not-found';
import AnnotationTopBarContainer from 'containers/annotation-page/top-bar/top-bar';
import { Workspace } from 'reducers';
import { usePrevious } from 'utils/hooks';
import { readLatestFrame } from 'utils/remember-latest-frame';
import { SAVING_OVERLAY_CLASS } from 'utils/mousetrap-react';
import { isReferenceJob } from 'utils/reference-job';
import { shownJob } from 'utils/workspace-route';
import StepsProviderContainer from 'components/annotation-page/workspace/steps/steps-provider-container';
import SceneProviderContainer from 'components/annotation-page/workspace/scene/scene-provider-container';
import ReferencesProviderContainer from 'components/annotation-page/workspace/references/references-provider-container';
import ReferencesBusyOverlay from 'components/annotation-page/workspace/references/references-busy-overlay';
import ModelPickerHost from 'components/annotation-page/workspace/references/model-picker-host';
import RelabelFillContainer from 'components/annotation-page/workspace/relabel-fill-container';
import ModelCardContainer from 'components/annotation-page/workspace/model-card-container';
import { PreviewModeProvider } from 'components/annotation-page/workspace/preview-mode';
import StepsReloadOverlay from 'components/annotation-page/workspace/steps/steps-reload-overlay';
import { t } from 'utils/i18n';

interface Props {
    job: any | null | undefined;
    staleJob?: any | null;
    fetching: boolean;
    frameNumber: number;
    workspace: Workspace;
    saving: boolean;
    getJob(): void;
    saveLogs(): void;
    closeJob(): void;
    changeFrame(frame: number): void;
    hydrateColorSettings(jobId?: number | null, frame?: number | null): void;
}

// which object has no labels, in the UI language
function noLabelsText(job: { projectId: number | null; taskId: number; referenceId?: number | null }): string {
    if (isReferenceJob(job)) return t('Reference {id} does not contain any label.', { id: job.referenceId as number });
    if (job.projectId) return t('Project {id} does not contain any label.', { id: job.projectId });
    return t('Task {id} does not contain any label.', { id: job.taskId });
}

export default function AnnotationPageComponent(props: Props): JSX.Element {
    const {
        job,
        staleJob = null,
        fetching,
        workspace,
        frameNumber,
        saving,
        getJob,
        closeJob,
        saveLogs,
        changeFrame,
        hydrateColorSettings,
    } = props;
    const prevJob = usePrevious(job);
    const prevFetching = usePrevious(fetching);

    const history = useHistory();
    useEffect(() => {
        saveLogs();
        const root = window.document.getElementById('root');
        if (root) {
            root.style.minHeight = '720px';
        }

        return () => {
            saveLogs();
            if (root) {
                root.style.minHeight = '';
            }

            if (!history.location.pathname.includes('/jobs')) {
                closeJob();
            }
        };
    }, []);

    useEffect(() => {
        if (job === null && !fetching) {
            getJob();
        }
    }, [job, fetching]);

    useEffect(() => {
        if (job?.id) {
            hydrateColorSettings(job.id, frameNumber);
        }
    }, [job?.id, frameNumber]);

    useEffect(() => {
        if (prevFetching && !fetching && !prevJob && job) {
            const latestFrame = readLatestFrame(job.id);

            if (typeof latestFrame === 'number' &&
                latestFrame !== frameNumber &&
                latestFrame >= job.startFrame &&
                latestFrame <= job.stopFrame
            ) {
                const notificationKey = `cvat-notification-continue-job-${job.id}`;
                notification.info({
                    key: notificationKey,
                    message: t('You finished working on frame {n}', { n: latestFrame }),
                    description: (
                        <span>
                            {t('Press')}
                            <Button
                                className='cvat-notification-continue-job-button'
                                type='link'
                                onClick={() => {
                                    changeFrame(latestFrame);
                                    notification.close(notificationKey);
                                }}
                            >
                                {t('here')}
                            </Button>
                            {t('if you would like to continue')}
                        </span>
                    ),
                    placement: 'topRight',
                    className: 'cvat-notification-continue-job',
                });
            }

            if (!job.labels.length) {
                notification.warning({
                    message: t('No labels'),
                    description: (
                        <span>
                            {`${noLabelsText(job)} `}
                            <a href={isReferenceJob(job) ?
                                `/references/${job.referenceId}` :
                                `/${job.projectId ? 'projects' : 'tasks'}/${job.projectId || job.taskId}/`}
                            >
                                {t('Add')}
                            </a>
                            {` ${t('the first one for editing annotation.')}`}
                        </span>
                    ),
                    placement: 'topRight',
                    className: 'cvat-notification-no-labels',
                });
            }
        }
    }, [job, fetching, prevJob, prevFetching]);

    const shown = shownJob(job, staleJob);
    if (shown === null) {
        return <Spin size='large' className='cvat-spinner' />;
    }

    if (typeof shown === 'undefined') {
        return <JobNotFound />;
    }

    return (
        // remounted per job: the bar and the canvas bind to the job they were created with
        <Layout className='cvat-annotation-page' key={`${isReferenceJob(shown) ? 'reference' : 'task'}-${shown.id}`}>
            {/* the header's Scene button opens the panel in the workspace sidebar */}
            <SceneProviderContainer>
                {/* own classes: the reference page styles the shared cvat-annotation-* ones globally */}
                <Layout.Header className='cvat-annotation-header cvat-workspace-header'>
                    <AnnotationTopBarContainer />
                </Layout.Header>
                {/* steps state is shared by the sidebar's Steps tab and the filmstrip dots */}
                <StepsProviderContainer>
                    <ReferencesProviderContainer>
                        <PreviewModeProvider>
                            <Layout.Content className='cvat-annotation-layout-content cvat-workspace-content'>
                                {workspace === Workspace.STANDARD3D && <StandardWorkspace3DComponent />}
                                {workspace === Workspace.STANDARD && <StandardWorkspaceComponent />}
                                {workspace === Workspace.ATTRIBUTE_ANNOTATION && <AttributeAnnotationWorkspace />}
                                {workspace === Workspace.TAG_ANNOTATION && <TagAnnotationWorkspace />}
                                {workspace === Workspace.REVIEW_WORKSPACE && <ReviewAnnotationsWorkspace />}
                                {workspace !== Workspace.STANDARD3D && (
                                    isReferenceJob(shown) ? <ReferenceStripContainer /> : <FrameStripContainer />
                                )}
                                {job === null && (
                                    <div className={`${SAVING_OVERLAY_CLASS} cvat-workspace-job-loading`}>
                                        <Spin size='large' />
                                    </div>
                                )}
                                {/* an edit landing mid-save would be marked saved without reaching the server */}
                                {saving && <div className={SAVING_OVERLAY_CLASS} />}
                                <StepsReloadOverlay />
                                <ReferencesBusyOverlay />
                                <ModelPickerHost />
                                <RelabelFillContainer />
                                <ModelCardContainer />
                            </Layout.Content>
                        </PreviewModeProvider>
                    </ReferencesProviderContainer>
                </StepsProviderContainer>
            </SceneProviderContainer>
            <FiltersModalComponent />
            <StatisticsModalComponent />
        </Layout>
    );
}
